import {
  Tr,
  Th,
  Text,
  Button,
  HStack,
  VStack,
  Badge,
  Icon,
  Image,
  Tooltip,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { CheckIcon, SettingsIcon } from '@chakra-ui/icons';
import { MdFilterList } from 'react-icons/md';

import type {
  AvailableCarrier,
  CarrierQuote,
  RiskDetailsSync,
} from 'api/quote-marketplace';
import type { QuoteMarketplaceTheme, CarrierSortOption } from '../../hooks';
import bigLogo from 'assets/img/carriers/big-logo.png';
import { RISK_DETAILS_CAPTURING_TOOLTIP } from '../../constants';
import RiskDetailsSyncProgress from './RiskDetailsSyncProgress';
import {
  STICKY_LABEL_WIDTH,
  STICKY_REQUESTED_WIDTH,
  STICKY_SELECTED_LEFT,
} from './CarrierCell';

const toColorVar = (color: string) =>
  `var(--chakra-colors-${color.replace('.', '-')})`;

const selectedBorderStyles = (borderColorVar: string) => ({
  content: '""',
  position: 'absolute',
  top: '-1px',
  bottom: '-1px',
  width: '2px',
  bg: borderColorVar,
  zIndex: 10,
  pointerEvents: 'none',
});

interface ComparisonTableHeaderProps {
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  selectedCarrierQuoteId: number | null;
  onSelectCarrier: (carrierQuoteId: number) => void;
  onOpenMarketOptions: () => void;
  onGetQuotes: () => void;
  isLoading: boolean;
  isSelectingCarrier: boolean;
  isGettingQuotes: boolean;
  canGetQuotes: boolean;
  isCapturingRiskDetails?: boolean;
  riskDetailsSync?: RiskDetailsSync;
  theme: QuoteMarketplaceTheme;
  sortBy?: CarrierSortOption;
  onSortChange?: (sort: CarrierSortOption) => void;
}

export const ComparisonTableHeader = ({
  availableCarriers,
  carrierQuotes,
  selectedCarrierQuoteId,
  onSelectCarrier,
  onOpenMarketOptions,
  onGetQuotes,
  isLoading,
  isSelectingCarrier,
  isGettingQuotes,
  canGetQuotes,
  isCapturingRiskDetails = false,
  riskDetailsSync,
  theme,
  sortBy = 'price',
  onSortChange,
}: ComparisonTableHeaderProps) => {
  const isGetQuotesDisabled =
    !canGetQuotes || isLoading || isGettingQuotes || isCapturingRiskDetails;

  const getQuoteForCarrier = (
    carrier: AvailableCarrier,
  ): CarrierQuote | undefined => {
    return carrierQuotes.find((q) => q.acturis_product === carrier.product);
  };

  const isCarrierSelected = (carrier: AvailableCarrier): boolean => {
    const quote = getQuoteForCarrier(carrier);
    return quote?.id === selectedCarrierQuoteId;
  };

  const splitCarrierName = (name: string): [string, string] => {
    const words = name.trim().split(/\s+/);
    return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
  };

  const renderCarrierStatus = (carrier: AvailableCarrier) => {
    const quote = getQuoteForCarrier(carrier);

    if (quote?.status === 'quoted') {
      if (quote.is_credential_error) {
        return (
          <Tooltip
            label="Quote obtained but credentials may be invalid. Update credentials to ensure future quotes work."
            hasArrow
            placement="top"
            maxW="300px"
          >
            <Badge colorScheme="orange" fontSize="xs" cursor="help">
              ⚠ Credentials
            </Badge>
          </Tooltip>
        );
      }
      return null;
    }

    if (quote?.status === 'declined') {
      const reason = quote.decline_reason || 'No quote available';
      const colorScheme = quote.is_credential_error ? 'orange' : 'red';
      const label = quote.is_credential_error ? 'Credentials' : 'Declined';
      return (
        <Tooltip label={reason} hasArrow placement="top" maxW="300px">
          <Badge colorScheme={colorScheme} fontSize="xs" cursor="help">
            {label}
          </Badge>
        </Tooltip>
      );
    }

    if (quote?.status === 'error') {
      const message = quote.error_message || 'An error occurred';
      return (
        <Tooltip label={message} hasArrow placement="top" maxW="300px">
          <Badge colorScheme="orange" fontSize="xs" cursor="help">
            Error
          </Badge>
        </Tooltip>
      );
    }

    if (quote?.status === 'timed_out') {
      const message =
        quote.error_message ||
        'Timed out — the carrier did not return a quote in time';
      return (
        <Tooltip label={message} hasArrow placement="top" maxW="300px">
          <Badge colorScheme="gray" fontSize="xs" cursor="help">
            Timed out
          </Badge>
        </Tooltip>
      );
    }

    return null;
  };

  const renderSelectButton = (carrier: AvailableCarrier) => {
    const quote = getQuoteForCarrier(carrier);
    const isSelected = isCarrierSelected(carrier);

    if (
      !quote ||
      quote.status === 'declined' ||
      quote.status === 'error' ||
      quote.status === 'timed_out'
    ) {
      return null;
    }

    if (isSelected) {
      return (
        <Button
          size="xs"
          colorScheme="yellow"
          variant="solid"
          leftIcon={<CheckIcon boxSize={2} />}
          px={3}
        >
          Selected
        </Button>
      );
    }

    const canSelect = quote.status === 'quoted';

    return (
      <Button
        size="xs"
        variant="outline"
        colorScheme={canSelect ? 'blue' : 'gray'}
        isDisabled={!canSelect || isLoading}
        isLoading={isSelectingCarrier}
        onClick={() => onSelectCarrier(quote.id)}
        px={3}
      >
        Select
      </Button>
    );
  };

  return (
    <Tr>
      <Th
        position="sticky"
        left={0}
        bg={theme.headerBg}
        borderColor={theme.borderColor}
        py={3}
        zIndex={3}
        w="1px"
      >
        <VStack spacing={2} align="flex-start">
          <Image
            src={bigLogo}
            alt="BIG Insurance"
            height="48px"
            objectFit="contain"
          />
          <HStack spacing={1}>
            <Tooltip
              label={RISK_DETAILS_CAPTURING_TOOLTIP}
              hasArrow
              placement="top"
              isDisabled={!isCapturingRiskDetails}
            >
              <Box
                as="span"
                display="inline-block"
                cursor={isGetQuotesDisabled ? 'not-allowed' : undefined}
              >
                <Button
                  size="xs"
                  colorScheme="yellow"
                  onClick={onGetQuotes}
                  isLoading={isGettingQuotes || isCapturingRiskDetails}
                  loadingText="Get Quotes"
                  isDisabled={!canGetQuotes || isLoading}
                  pointerEvents={isGetQuotesDisabled ? 'none' : undefined}
                >
                  Get Quotes
                </Button>
              </Box>
            </Tooltip>
            <Button
              size="xs"
              variant="outline"
              leftIcon={<SettingsIcon boxSize={3} />}
              onClick={onOpenMarketOptions}
              isDisabled={isLoading}
            >
              Markets
            </Button>
            {onSortChange && (
              <Menu strategy="fixed">
                <MenuButton
                  as={Button}
                  size="xs"
                  variant="outline"
                  isDisabled={isLoading}
                  px={2}
                >
                  <Icon as={MdFilterList} boxSize={4} />
                </MenuButton>
                <MenuList fontSize="sm" minW="140px" zIndex={1500}>
                  <MenuItem
                    onClick={() => onSortChange('price')}
                    fontWeight={sortBy === 'price' ? 'semibold' : 'normal'}
                  >
                    Price
                  </MenuItem>
                  <MenuItem
                    onClick={() => onSortChange('coverages')}
                    fontWeight={sortBy === 'coverages' ? 'semibold' : 'normal'}
                  >
                    Coverages
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </HStack>
          <RiskDetailsSyncProgress sync={riskDetailsSync} />
        </VStack>
      </Th>
      <Th
        bg={theme.headerBg}
        borderColor={theme.borderColor}
        minW={`${STICKY_REQUESTED_WIDTH}px`}
        py={4}
        textAlign="center"
        position="sticky"
        left={`${STICKY_LABEL_WIDTH}px`}
        zIndex={2}
      />
      {availableCarriers.map((carrier, index) => {
        const isSelected = isCarrierSelected(carrier);
        const quote = getQuoteForCarrier(carrier);
        const isQuoted = quote?.status === 'quoted';
        const isSticky = index === 0 && isSelected;
        const borderColorVar = toColorVar(theme.selectedBorderColor);
        return (
          <Th
            key={carrier.carrier_id}
            borderColor={theme.borderColor}
            textAlign="center"
            minW="120px"
            py={4}
            bg={isSelected ? theme.selectedHeaderBg : theme.headerBg}
            overflow={isSelected ? 'visible' : undefined}
            position={isSticky ? 'sticky' : isSelected ? 'relative' : undefined}
            left={isSticky ? `${STICKY_SELECTED_LEFT}px` : undefined}
            zIndex={isSticky ? 3 : isSelected ? 1 : undefined}
            _before={
              isSelected
                ? { ...selectedBorderStyles(borderColorVar), left: 0 }
                : undefined
            }
            _after={
              isSelected
                ? { ...selectedBorderStyles(borderColorVar), right: 0 }
                : undefined
            }
          >
            <VStack spacing={2}>
              <HStack spacing={1} align="flex-start">
                {(() => {
                  const [firstLine, secondLine] = splitCarrierName(
                    carrier.display_name,
                  );
                  return (
                    <VStack spacing={0} align="center">
                      <Text
                        fontSize="sm"
                        fontWeight="600"
                        textTransform="none"
                        letterSpacing="normal"
                        lineHeight="short"
                        color={isSelected ? 'yellow.700' : undefined}
                      >
                        {firstLine}
                      </Text>
                      {secondLine && (
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          textTransform="none"
                          letterSpacing="normal"
                          lineHeight="short"
                          color={isSelected ? 'yellow.700' : undefined}
                        >
                          {secondLine}
                        </Text>
                      )}
                    </VStack>
                  );
                })()}
                {isQuoted && (
                  <Box
                    bg="green.500"
                    rounded="full"
                    p="1"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={CheckIcon} boxSize={2} color="white" />
                  </Box>
                )}
              </HStack>
              {renderCarrierStatus(carrier)}
              {renderSelectButton(carrier)}
            </VStack>
          </Th>
        );
      })}
    </Tr>
  );
};
